import { computed, ComputedRef, Ref, unref } from 'vue'
import { JobStatus } from '@/models/job'
import { ALLOWED_JOB_STATUS_FOR_READONLY_UI } from '@/components/jobs/navigation'

const FIELD_VIEW_JOB_ROUTE_NAMES = [
  'field-view-next',
  'fvnext-info-panel',
  'fvnext-job-request',
  'fvnext-project',
]

export const isFieldViewJobRouteName = (routeName: string | null | undefined) => {
  return routeName != null && FIELD_VIEW_JOB_ROUTE_NAMES.includes(routeName)
}

interface UseJobMapLinkOptions {
  jobNumber: Ref<number | undefined> | ComputedRef<number | undefined>
  jobStatus: Ref<JobStatus | null | undefined> | ComputedRef<JobStatus | undefined>
  parentRouteName: Ref<string> | ComputedRef<string>
}

interface UseJobMapLinkReturn {
  isOnMap: ComputedRef<boolean>
  showTripMap: ComputedRef<boolean>
  mapLink: ComputedRef<string | null>
}

/**
 * Composable for managing job map-related computed properties
 *
 * @param options - Configuration options
 * @param options.jobNumber - The job number (not UUID) (can be null for new jobs)
 * @param options.jobStatus - The current job status
 * @param options.parentRouteName - The name of the parent route
 * @returns Object containing isOnMap, showTripMap, and mapLink computed refs
 */
export function useJobMapLink({
  jobNumber,
  jobStatus,
  parentRouteName,
}: UseJobMapLinkOptions): UseJobMapLinkReturn {
  const isOnMap = computed(() => isFieldViewJobRouteName(unref(parentRouteName)))

  const showTripMap = computed(() => {
    const jobStatusValue = unref(jobStatus)
    return (
      jobStatusValue != null &&
      !isOnMap.value &&
      ALLOWED_JOB_STATUS_FOR_READONLY_UI.includes(jobStatusValue)
    )
  })

  const mapLink = computed(() => {
    if (!unref(jobNumber) || isOnMap.value) return null
    return `/map/jobs/${unref(jobNumber)}`
  })

  return {
    isOnMap,
    showTripMap,
    mapLink,
  }
}
